import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChildren, ElementRef, QueryList, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { Product, SelectedProduct, normalizeSalePrice } from '../../services/products';
import { Customer } from '../../services/customer';
import { SearchBarComponent } from '../../components/search-bar.component/search-bar-component';
import { MatDialog } from '@angular/material/dialog';
import { ImagePopupComponent } from '../shared/image-popup/image-popup';

@Component({
  selector: 'app-product-list',
  standalone: true,
  templateUrl: './product-list-component.html',
  styleUrls: ['./product-list-component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatIconModule,
    SearchBarComponent
  ]
})
export class ProductListComponent implements OnChanges  {
  @Input() selectedCustomer!: Customer;
  @Input() selectedProducts!: SelectedProduct[];
  @Input() products: Product[] = [];
  @Output() cartUpdated = new EventEmitter<SelectedProduct[]>();
  @ViewChildren('quantityInput') quantityInputs!: QueryList<ElementRef<HTMLInputElement>>;

  searchTerm: string = '';
  filteredProducts: Product[] = [];

  justFocusedProductId: number | null = null;

  constructor(
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['products']) {
      this.searchProducts(); // Will reapply filter using current searchTerm
    }
  }

  searchProducts(): void {
    this.filteredProducts = this.products.filter(product =>
      product.name.toLowerCase().includes(this.searchTerm.toLowerCase()));
  }

  trackByProductId(_index: number, product: Product): number {
    return product.id;
  }

  async addToCart(product: Product): Promise<void> {
    if (!this.isProductInCart(product)) {
      this.selectedProducts.push({
        productId: product.id,
        quantity: 0,
        price: normalizeSalePrice(product, product.list_price)
      });
      this.updateCart();

      // Force view update so input exists, then focus within same user interaction
      this.justFocusedProductId = product.id;
      this.cdr.detectChanges();
      this.focusQuantityInput(product);
      setTimeout(() => { this.justFocusedProductId = null; }, 50);
    }
  }

  async removeFromCart(product: Product): Promise<void> {
    this.selectedProducts = this.selectedProducts.filter(item => item.productId !== product.id);
    this.updateCart();
  }

  isProductInCart(product: Product): boolean {
    return this.selectedProducts.some(item => item.productId === product.id);
  }

  async decreaseQuantity(product: Product): Promise<void> {
    const index = this.selectedProducts.findIndex(item => item.productId === product.id);
    if (index !== -1) {
      this.selectedProducts[index].quantity--;
      if (this.selectedProducts[index].quantity <= 0) {
        await this.removeFromCart(product);
      } else {
        this.updateCart();
      }
    }
  }

  async increaseQuantity(product: Product): Promise<void> {
    const index = this.selectedProducts.findIndex(item => item.productId === product.id);
    if (index !== -1) {
      this.selectedProducts[index].quantity++;
      this.updateCart();
    }
  }

  updateCart(): void {
    this.cartUpdated.emit(this.selectedProducts);
  }

  get totalItems(): number {
    return this.selectedProducts.reduce((sum, _) => sum + 1, 0);
  }

  get totalAmount(): number {
    return this.selectedProducts.reduce((sum, item) => {
      const product = this.products.find(p => p.id === item.productId);
      return sum + (product?.list_price ?? 0) * item.quantity;
    }, 0);
  }

  openImagePopup(event: Event, imageBase64: string): void {
    event.stopPropagation(); // Prevent event propagation
    this.dialog.open(ImagePopupComponent, {
      data: { image: imageBase64 },
      panelClass: 'full-screen-popup', // Custom class for full-screen modal
      width: '100vw',  // Full width of the screen
      height: '100vh', // Full height of the screen
      // Do not include disableClose, so it can be closed by clicking outside
      hasBackdrop: true,  // Ensure there's a backdrop
      backdropClass: 'backdrop-clickable' // Optional: add a class to style the backdrop
    });
  }

  getSelectedProduct(product: Product): { productId: number, quantity: number } {
    const match = this.selectedProducts.find(item => item.productId === product.id);
    return match ?? { productId: 0, quantity: 0 };
  }

  private focusQuantityInput(product: Product): void {
    if (!this.quantityInputs || this.quantityInputs.length === 0) {
      return;
    }

    // Find the input by the data-product-id to avoid index mismatch
    const inputsArray = this.quantityInputs.toArray();
    const targetInputRef = inputsArray.find(ref => {
      const el = ref.nativeElement as HTMLInputElement;
      return el.getAttribute('data-product-id') === String(product.id);
    });

    if (!targetInputRef) {
      return;
    }

    const inputEl = targetInputRef.nativeElement as HTMLInputElement;

    // Mobile-friendly focus sequence
    // Ensure element is visible within viewport
    inputEl.scrollIntoView({ block: 'nearest', inline: 'nearest' });

    // Attempt immediate focus within the same tick to keep it tied to user gesture
    try { inputEl.focus(); } catch {}

    // Some browsers require delayed selection to apply
    setTimeout(() => {
      try {
        const value = String(inputEl.value ?? '');
        inputEl.setSelectionRange(0, value.length);
        // Fallback for iOS: toggle readonly to force keyboard
        const wasReadOnly = inputEl.readOnly;
        inputEl.readOnly = true;
        inputEl.readOnly = false;
        inputEl.focus();
      } catch {}
    }, 0);
  }
}
